//frontend/src/components/admin/UserView.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
    User, 
    Mail, 
    Calendar, 
    Package, 
    MapPin, 
    Phone, 
    Clock,
    CreditCard,
    RefreshCw,
    Truck,
    DollarSign,
    CircleUserRound,
    ShoppingBag
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import MetaData from '../layouts/MetaData';

const UserView = () => {
    const [user, setUser] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        fetchUserDetails();
    }, [id]);

    const fetchUserDetails = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`/api/v1/admin/user/${id}`, {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true
            });
            
            if (data.success) {
                setUser(data.user);
                setOrders(data.orders || []);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching user details');
            if (error.response?.status === 401) {
                navigate('/admin/login');
            }
        } finally {
            setLoading(false);
        }
    };

    const OrderStatusBadge = ({ status }) => {
        const getStatusConfig = (status) => {
            switch(status) {
                case 'Order Delivered':
                    return { bg: 'bg-green-100', text: 'text-green-800', icon: Truck };
                case 'Order Cancelled':
                    return { bg: 'bg-red-100', text: 'text-red-800', icon: Package };
                default:
                    return { bg: 'bg-blue-100', text: 'text-blue-800', icon: Package };
            }
        };

        const config = getStatusConfig(status);
        const Icon = config.icon;

        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text} gap-1`}>
                <Icon className="w-4 h-4" />
                {status}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <RefreshCw className="h-8 w-8 text-[#dc2626] animate-spin" />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <CircleUserRound className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-lg font-medium text-gray-900">User not found</h3>
                    <p className="mt-1 text-sm text-gray-500">The requested user profile does not exist.</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <MetaData title={`${user.name}'s Profile`} />
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Card className="bg-white rounded-xl shadow-lg overflow-hidden">
                        {/* Header Section */}
                        <div className="bg-[#dc2626] px-6 py-8 sm:px-8">
                            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                                <div className="relative flex-shrink-0">
                                    {user.avatar ? (
                                        <img
                                            src={user.avatar}
                                            alt={user.name}
                                            className="h-24 w-24 rounded-full object-cover border-4 border-white shadow-md"
                                        />
                                    ) : (
                                        <div className="h-24 w-24 rounded-full bg-white flex items-center justify-center border-4 border-white shadow-md">
                                            <User className="h-12 w-12 text-gray-400" />
                                        </div>
                                    )}
                                    <span className={`absolute bottom-0 right-0 w-5 h-5 rounded-full border-2 border-white ${
                                        user.isBlocked ? 'bg-red-500' : 'bg-green-500'
                                    }`} />
                                </div>
                                <div className="text-center sm:text-left">
                                    <h1 className="text-2xl font-bold text-white font-['Crimson_Text']">
                                        {user.name}
                                    </h1>
                                    <p className="text-red-100 mt-1">{user.email}</p>
                                    <div className="mt-3">
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                            user.isBlocked 
                                                ? 'bg-red-100 text-red-800' 
                                                : 'bg-green-100 text-green-800'
                                        }`}>
                                            {user.isBlocked ? 'Blocked' : 'Active'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="p-6 sm:p-8 space-y-8">
                            {/* Personal Information */}
                            <div>
                                <h2 className="text-xl font-bold text-[#dc2626] mb-6 font-['Crimson_Text']">
                                    Personal Information
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="flex items-start space-x-3">
                                        <User className="w-5 h-5 text-gray-400 mt-0.5" />
                                        <div>
                                            <p className="text-sm text-gray-500">Full Name</p>
                                            <p className="font-medium text-gray-900">{user.name}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                                        <div>
                                            <p className="text-sm text-gray-500">Email Address</p>
                                            <p className="font-medium text-gray-900">{user.email}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                                        <div>
                                            <p className="text-sm text-gray-500">Joined On</p>
                                            <p className="font-medium text-gray-900">
                                                {new Date(user.createdAt).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Order History */}
                            <div>
                                <h2 className="text-xl font-bold text-[#dc2626] mb-6 font-['Crimson_Text']">
                                    Order History
                                </h2>
                                {orders.length > 0 ? (
                                    <div className="space-y-6">
                                        {orders.map(order => (
                                            <div key={order._id} 
                                                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                                            >
                                                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
                                                    <div>
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <h4 className="text-lg font-semibold text-gray-900">
                                                                Order #{order.orderId}
                                                            </h4>
                                                            <OrderStatusBadge status={order.orderStatus} />
                                                        </div>
                                                        <div className="flex items-center gap-4 text-sm text-gray-500">
                                                            <span className="flex items-center gap-1">
                                                                <Calendar className="w-4 h-4" />
                                                                {new Date(order.createdAt).toLocaleDateString()}
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <DollarSign className="w-4 h-4" />
                                                                ₹{order.totalPrice}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div className="space-y-4">
                                                        <div className="flex items-start space-x-3">
                                                            <CreditCard className="w-5 h-5 text-gray-400 mt-0.5" />
                                                            <div>
                                                                <p className="text-sm text-gray-500">Payment Details</p>
                                                                <p className="font-medium text-gray-900">
                                                                    {order.paymentInfo.paymentMethod}
                                                                </p>
                                                                <p className={`text-sm ${
                                                                    order.paymentInfo.status === 'succeeded' 
                                                                        ? 'text-green-600' 
                                                                        : 'text-yellow-600'
                                                                }`}>
                                                                    {order.paymentInfo.status === 'succeeded' ? 'Paid' : 'Pending'}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-start space-x-3">
                                                            <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                                                            <div>
                                                                <p className="text-sm text-gray-500">Delivery Address</p>
                                                                <p className="font-medium text-gray-900">
                                                                    {order.shippingInfo.address}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <div className="flex items-center mb-3">
                                                            <ShoppingBag className="w-5 h-5 text-gray-400 mr-2" />
                                                            <p className="font-medium text-gray-900">Order Items</p>
                                                        </div>
                                                        <div className="space-y-4">
                                                            {order.orderItems.map((item, index) => (
                                                                <div key={index} className="flex items-center">
                                                                    <img
                                                                        src={item.image}
                                                                        alt={item.name}
                                                                        className="w-16 h-16 object-cover rounded-lg"
                                                                    />
                                                                    <div className="ml-4">
                                                                        <p className="font-medium text-gray-900">
                                                                            {item.name}
                                                                        </p>
                                                                        <p className="text-sm text-gray-500">
                                                                            {item.quantity} x ₹{item.price}
                                                                        </p>
                                                                        <p className="text-sm font-medium text-[#dc2626]">
                                                                            Total: ₹{item.quantity * item.price}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                                        <Package className="mx-auto h-12 w-12 text-gray-400" />
                                        <h3 className="mt-2 text-lg font-medium text-gray-900">No Orders Yet</h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            This user hasn't placed any orders yet.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default UserView;